import React, { useState } from 'react';
import T from 'prop-types';
import cx from 'classnames';

import SegmentDropdown from './SegmentDropdown';
import Tooltip from '../../../../components/Tooltip';
import ThresholdBadge from '../../../../components/ThresholdBadge';

const renderThresholdCell = (value, oppositeColor) => (
  <ThresholdBadge
    value={value?.toFixed(1)}
    threshold={0}
    valueUnit="%"
    oppositeColor={oppositeColor}
  />
);

const defaultColumns = [
  { text: '', dataField: 'name' },
  { text: '90-Day', dataField: '90_days', renderCell: renderThresholdCell },
  { text: '30-Day', dataField: '30_days', renderCell: renderThresholdCell },
  { text: '7-Day', dataField: '7_days', renderCell: renderThresholdCell },
];

const PerformanceSnapshotTable = ({
  data,
  columns = defaultColumns,
  showHeader,
  customHeader,
  releasedNotification,
  className,
}) => (
  <div className={cx('performance-shanpshot-table', className)}>
    {showHeader && (
      <header className="performance-shanpshot-table__header">
        {customHeader || (
          <>
            <h5 className="h5 performance-shanpshot-table__header-title">{data.title}</h5>
            {data.subtitle && (
              <p className="performance-shanpshot-table__header-subtitle">({data.subtitle})</p>
            )}
            {data.tooltip && (
              <Tooltip content={data.tooltip}>
                <span className="hicon-question-circle" />
              </Tooltip>
            )}
          </>
        )}
      </header>
    )}
    <div className="performance-shanpshot-table__body">
      {releasedNotification || (
        <table className="table">
          <thead className="thead">
            <tr>
              {columns.map(({ text, dataField }) => (
                <th key={dataField}>{text}</th>
              ))}
            </tr>
          </thead>
          <tbody className="tbody">
            {data?.metrics?.map((row, i) => (
              <tr key={i}>
                {columns.map(({ dataField, renderCell }) => (
                  <td key={dataField}>
                    {renderCell ? renderCell(row[dataField], row.opposite_color) : row[dataField]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  </div>
);

export const PerformanceSnapshotTableWithSegmentDropdown = ({
  columns,
  data,
  showHeader,
  releasedNotification,
  className,
}) => {
  const impactSegment = data.find((item) => item.impact);
  const [active, setActive] = useState((impactSegment || data[0])?.cohort);

  const mapDataByCohort = data.reduce(
    (result, item) => ({
      ...result,
      [item.cohort]: item,
    }),
    {}
  );

  const renderedCustomHeader = <SegmentDropdown active={active} data={data} onChange={setActive} />;

  return (
    <PerformanceSnapshotTable
      data={mapDataByCohort[active]}
      columns={columns}
      showHeader={showHeader}
      customHeader={renderedCustomHeader}
      releasedNotification={releasedNotification}
      className={className}
    />
  );
};

const PerformanceSnapshotTableProps = {
  data: T.shape({
    title: T.string,
    subtitle: T.string,
    tooltip: T.string,
    metrics: T.arrayOf(T.object),
  }).isRequired,
  columns: T.arrayOf(T.object),
  showHeader: T.bool,
  customHeader: T.node,
  releasedNotification: T.node,
  className: T.string,
};

PerformanceSnapshotTable.propTypes = PerformanceSnapshotTableProps;

PerformanceSnapshotTableWithSegmentDropdown.propTypes = {
  ...PerformanceSnapshotTableProps,
  data: T.arrayOf(PerformanceSnapshotTableProps.data).isRequired,
};

export default PerformanceSnapshotTable;
